/**
 * @fileoverview Generic page header section.
 * @description The optional small uppercase "kicker" label + accent underline, page title, and description
 * that opens a dashboard-area page, optionally trailed by a filters slot (period/branch/status
 * dropdowns rendered on the same row, right-aligned, on pages that have page-level filters).
 * Originally built for the sales module (every one of its pages opens with this exact shape) but
 * has no sales-specific coupling, so it lives here as a shared molecule any module can reuse.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import type { ReactNode } from 'react'

interface PageHeaderProps {
  kicker?: string | undefined
  title: string
  description: string
  filters?: ReactNode
  actions?: ReactNode
}

/**
 * @component
 * @param {PageHeaderProps} props - The section's kicker/title/description plus an optional
 * filters slot.
 * @param {string} [props.kicker] - The small uppercase label above the title (e.g. "OVERVIEW");
 * the label and its accent underline are omitted when a page passes none.
 * @param {string} props.title - The page's h1 heading.
 * @param {string} props.description - The one-line description under the title.
 * @param {ReactNode} [props.filters] - Right-aligned filter controls rendered alongside the
 * heading, on pages that have page-level filters (not table filters).
 * @param {ReactNode} [props.actions] - Right-aligned page actions (a primary button such as Add
 * User) rendered after any filters, so an action never has to be passed through `filters`.
 */
function PageHeader({ kicker, title, description, filters, actions }: PageHeaderProps) {
  return (
    <section className="flex flex-wrap items-end gap-5 sm:gap-7">
      <div className="flex min-w-0 flex-1 basis-80 flex-col gap-2">
        {kicker && (
          <div className="inline-flex flex-col self-start gap-1">
            <span
              className="text-[11.5px] font-bold tracking-[0.9px] text-kicker"
              data-testid="page-header-kicker"
            >
              {kicker}
            </span>
            <div className="h-[2.5px] w-full rounded-sm bg-primary" />
          </div>
        )}
        <h1
          className="text-[22px] font-bold tracking-[-0.5px] text-ink sm:text-[26px]"
          data-testid="page-header-title"
        >
          {title}
        </h1>
        <p
          className="max-w-description text-compact-lg leading-relaxed text-slate"
          data-testid="page-header-description"
        >
          {description}
        </p>
      </div>
      {filters && <div className="flex flex-wrap items-end gap-3.5">{filters}</div>}
      {actions && <div className="flex flex-wrap items-end gap-3.5">{actions}</div>}
    </section>
  )
}

export default PageHeader
